import { InvalidateQueryFilters, skipToken, useQuery } from '@tanstack/react-query';
import { chunk } from 'lodash-es';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useBillingLines from '~hooks/useBillingLines/useBillingLines';

export const GROUP_BILLING_LINES_QUERY_KEY = 'groupBillingLines';

export function makeGroupBillingLinesWithJobsFilters(
  jobIds: string[],
): InvalidateQueryFilters {
  const changedJobIds = new Set(jobIds);

  return {
    predicate: ({ queryKey: [key, , groupJobIds] }) =>
      key === GROUP_BILLING_LINES_QUERY_KEY &&
      Array.isArray(groupJobIds) &&
      groupJobIds.some(
        (jobId: unknown) => typeof jobId === 'string' && changedJobIds.has(jobId),
      ),
  };
}

// Reads every billing line of a group's jobs, only once `isEnabled` turns on: a group
// header shows totals from the groups endpoint and needs its lines only to select them
export default function useGroupBillingLinesQuery(
  companyId: string,
  jobIds: string[],
  isEnabled: boolean,
) {
  const { fetchAllBillingLinesForJobs } = useBillingLines();
  const canReadLines = isEnabled && Boolean(companyId) && jobIds.length > 0;

  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: [GROUP_BILLING_LINES_QUERY_KEY, companyId, jobIds],
    queryFn: canReadLines
      ? async ({ signal }) => {
          const linesPerJobIdsChunk = await Promise.all(
            chunk(jobIds, MAX_API_PAGE_SIZE).map((jobIdsChunk) =>
              fetchAllBillingLinesForJobs(companyId, jobIdsChunk, signal),
            ),
          );

          return linesPerJobIdsChunk.flat();
        }
      : skipToken,
  });

  return { isError, isFetching, lines: data ?? null, refetch };
}
